import {
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { UserProvider } from "./contexts/UserContext";
import AppContainer from "./components/AppContainer";
import AppInitializer from "./components/AppInitializer";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import { initializeStatusBar, initializePlatformStyles } from "./utils/statusBar";
import { initStorage } from "./services/storage.service";
import { useEffect, useState } from "react";

import Home from './pages/Home';
import TestPage from './pages/TestPage';

import UserMode from './pages/UserMode';
import UserHistory from './pages/UserHistory';
import EditProfile from './pages/EditProfile';

import SurveyMode from './pages/SurveyMode';
import NewPerson from './pages/NewPerson';
import PersonList from './pages/PersonList';
import AllLogs from './pages/AllLogs';

import SurveyStart from './pages/SurveyStart';
import LogStart from './pages/LogStart';

import AnyFishToday from './pages/Log/AnyFishToday';
import AnyFishYesterday from './pages/Log/AnyFishYesterday';
import WhenLastTime from './pages/Log/WhenLastTime';
import WhichMeal from './pages/Log/WhichMeal';
import WhichFish from './pages/Log/WhichFish';
import WhichRecipe from './pages/Log/WhichRecipe';
import WhichAmount from "./pages/Log/WhichAmount";
import ExtraInfo from './pages/Log/ExtraInfo';
import ReviewFishItem from "./pages/Log/ReviewFishItem";
import AddAnotherFishItem from './pages/Log/AddAnotherFishItem';
import ReviewLog from './pages/Log/ReviewLog';
import Submitted from './pages/Log/Submitted';
import AddAnotherDay from "./pages/Log/AddAnotherDay";
import AbortLog from './pages/Log/AbortLog';
import LogHistoryCalendarView from "./pages/LogHistoryCalendarView";
import LogHistoryDateLog from "./pages/LogHistoryDateLog";
import LogHistoryMonthlyStats from "./pages/LogHistoryMonthlyStats";

// Auth Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import SettingsPage from './pages/SettingsPage';
import SetupPinPage from './pages/SetupPinPage';
import ManageOrphanedRecords from './pages/ManageOrphanedRecords';
import ViewLogs from './pages/ViewLogs';


function App() {
  const location = useLocation();
  const [storageReady, setStorageReady] = useState(false);
  
  // Enable Android back button handling
  useAndroidBackButton();

  // Initialize platform-specific styles and status bar on app start
  useEffect(() => {
    const init = async () => {
      initializePlatformStyles(); // Set CSS variables (runs synchronously)
      initializeStatusBar();       // Configure native status bar (async, only on native)
      await initStorage();         // Initialize correct storage adapter (MUST AWAIT)
      setStorageReady(true);
    };
    init();
  }, []);

  // Wait for storage to be ready before rendering app
  if (!storageReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  const PageWrapper = ({ children }: { children: React.ReactNode }) => (
    <motion.div
      className="flex flex-col w-full grow"
      initial={{ opacity: 0, y: 5, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -5, scale: 0.98 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );

  return (
    <UserProvider>
      <AppInitializer>
        <AppContainer>
          <AnimatePresence mode="wait">
            <Routes>
              {/* Auth Routes (outside AppContainer nav) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/setup-pin" element={<SetupPinPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/manage-orphaned-records" element={<ManageOrphanedRecords />} />
              <Route path="/view-logs" element={<ViewLogs />} />

              {/* Main App Routes */}
              <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
              <Route path="/test-page" element={<PageWrapper><TestPage /></PageWrapper>} />
              <Route path="/profile/:id" element={<PageWrapper><EditProfile /></PageWrapper>} />

            {/* User Mode */}
            <Route path="/user" element={<PageWrapper><UserMode /></PageWrapper>} />
            <Route path="/user/history" element={<PageWrapper><UserHistory /></PageWrapper>} />    

            <Route path="/log-history/:personId" element={<LogHistoryCalendarView />} />
            <Route path="/log-history/:personId/monthly-stats" element={<LogHistoryMonthlyStats />} />
            <Route path="/log-history/:personId/:date" element={<LogHistoryDateLog />} />      

              {/* User Log Flow */}
            <Route path="/user/new-log/:date?" element={<LogStart />}>
              <Route index element={<Navigate to="any-fish-today" replace />} />
              <Route path="any-fish-today" element={<PageWrapper><AnyFishToday /></PageWrapper>} />
              <Route path="any-fish-yesterday" element={<PageWrapper><AnyFishYesterday /></PageWrapper>} />
              <Route path="when-last-time" element={<PageWrapper><WhenLastTime /></PageWrapper>} />
              <Route path="which-meal" element={<PageWrapper><WhichMeal /></PageWrapper>} />
              <Route path="which-fish" element={<PageWrapper><WhichFish /></PageWrapper>} />
              <Route path="which-recipe" element={<PageWrapper><WhichRecipe /></PageWrapper>} />
              <Route path="which-amount" element={<PageWrapper><WhichAmount /></PageWrapper>} />
              <Route path="extra-info" element={<PageWrapper><ExtraInfo /></PageWrapper>} />
              <Route path="review-fish-item" element={<PageWrapper><ReviewFishItem /></PageWrapper>} />
              <Route path="add-another-fish-item" element={<PageWrapper><AddAnotherFishItem /></PageWrapper>} />
              <Route path="review-log" element={<PageWrapper><ReviewLog /></PageWrapper>} />
              <Route path="submitted" element={<PageWrapper><Submitted /></PageWrapper>} />
              <Route path="add-another-day" element={<PageWrapper><AddAnotherDay /></PageWrapper>} />
              <Route path="abort" element={<PageWrapper><AbortLog /></PageWrapper>} />
            </Route>
            
            {/* Survey Mode */}

            <Route path="/survey" element={<PageWrapper><SurveyMode /></PageWrapper>} />
            <Route path="/survey/new-person" element={<PageWrapper><NewPerson /></PageWrapper>} />
            <Route path="/survey/person-list" element={<PageWrapper><PersonList /></PageWrapper>} />
            <Route path="/survey/all-logs" element={<PageWrapper><AllLogs /></PageWrapper>} />

            {/* Survey Flow */}
            <Route path="/survey/new/:id/:date?" element={<SurveyStart />}>
              <Route index element={<Navigate to="any-fish-today" replace />} />
              <Route path="any-fish-today" element={<PageWrapper><AnyFishToday /></PageWrapper>} />
              <Route path="any-fish-yesterday" element={<PageWrapper><AnyFishYesterday /></PageWrapper>} />
              <Route path="when-last-time" element={<PageWrapper><WhenLastTime /></PageWrapper>} />
              <Route path="which-meal" element={<PageWrapper><WhichMeal /></PageWrapper>} />
              <Route path="which-fish" element={<PageWrapper><WhichFish /></PageWrapper>} />
              <Route path="which-recipe" element={<PageWrapper><WhichRecipe /></PageWrapper>} />
              <Route path="which-amount" element={<PageWrapper><WhichAmount /></PageWrapper>} />
              <Route path="extra-info" element={<PageWrapper><ExtraInfo /></PageWrapper>} />
              <Route path="review-fish-item" element={<PageWrapper><ReviewFishItem /></PageWrapper>} />
              <Route path="add-another-fish-item" element={<PageWrapper><AddAnotherFishItem /></PageWrapper>} />
              <Route path="review-log" element={<PageWrapper><ReviewLog /></PageWrapper>} />
              <Route path="submitted" element={<PageWrapper><Submitted /></PageWrapper>} />
              <Route path="add-another-day" element={<PageWrapper><AddAnotherDay /></PageWrapper>} />
              <Route path="abort" element={<PageWrapper><AbortLog /></PageWrapper>} />
            </Route>
          </Routes>            
        </AnimatePresence>
      </AppContainer>
      </AppInitializer>
    </UserProvider>
  );
}

export default App;
